/**
 * CompanyResult — read-only card shown under the GSTIN field on S03 after a
 * successful registry lookup. Header (legal name + active pill) + 5 metadata
 * cells (GSTIN, PAN, state/city, registered date, address).
 */
import * as React from "react";
import { format, parseISO } from "date-fns";
import type { z } from "zod";
import type { onboarding } from "@flint/api-contract";

type Company = z.infer<typeof onboarding.lookupGstinOutput>;

export interface CompanyResultProps {
  gstin: string;
  company: Company;
}

export function CompanyResult({ gstin, company }: CompanyResultProps) {
  return (
    <div className="p-3.5 rounded bg-bg ring-1 ring-line/80 animate-in fade-in" aria-live="polite">
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <h2 className="text-base leading-[20px] font-semibold text-ink m-0">
            {company.legalName}
          </h2>
          <p className="m-0 mt-0.5 text-xs leading-[16px] text-muted">
            {company.tradeName ?? company.legalName} · {company.type}
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ok-100 text-ok text-xs font-medium whitespace-nowrap">
          <span aria-hidden className="w-1 h-1 rounded-full bg-ok" />
          Active
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-5 gap-y-2 pt-2.5 border-t border-line">
        <Cell label="GSTIN" value={<span className="font-mono tabular-nums">{gstin}</span>} />
        <Cell label="PAN" value={<span className="font-mono tabular-nums">{company.pan}</span>} />
        <Cell label="State" value={`${company.city}, ${company.state}`} />
        <Cell label="Registered" value={formatRegistered(company.registeredOn)} />
        <Cell
          label="Address"
          value={`${company.address}, ${company.city} ${company.pincode}`}
          full
        />
      </div>
    </div>
  );
}

function Cell({ label, value, full }: { label: string; value: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : undefined}>
      <span className="block text-xs uppercase tracking-[0.12em] text-muted mb-0.5">
        {label}
      </span>
      <div className="text-xs leading-[16px] font-medium text-ink">{value}</div>
    </div>
  );
}

function formatRegistered(value: string | null) {
  if (!value) return "Just now";
  return format(parseISO(value), "d MMM yyyy");
}
